export const systemPackIds = ["dice", "rpg", "survival", "relationship", "dynamic_world"] as const;

export type SystemPackId = typeof systemPackIds[number];
export type SystemPackSettings = Record<SystemPackId, boolean>;

/**
 * Freeform is the product baseline. Optional mechanics must be explicitly
 * enabled per experience/session and may never become an implicit dependency
 * of narration.
 */
export const freeformSystemPacks: SystemPackSettings = {
  dice: false,
  rpg: false,
  survival: false,
  relationship: false,
  dynamic_world: false,
};

export function normalizeSystemPackSettings(value: unknown): SystemPackSettings {
  const source = value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
  return Object.fromEntries(systemPackIds.map((id) => [id, source[id] === true])) as SystemPackSettings;
}

export interface AgentSystemPack {
  id: SystemPackId | string;
  label: string;
  instruction: string;
  enabled: boolean;
}

const optionalInstructions: Record<SystemPackId, string> = {
  dice: "Use dice only when the configured rule or user explicitly calls for a roll. Never invent a D&D-specific die or target number.",
  rpg: "Track only mechanics declared by the active File Types. Keep the rules system-neutral unless an explicit optional ruleset says otherwise.",
  survival: "Track declared survival resources and environmental pressure from canonical state; do not create meters that are absent from the schema.",
  relationship: "Track relationship changes as explicit consequences grounded in dialogue and events, without turning all social scenes into numeric checks.",
  dynamic_world: "Advance clocks, factions and off-screen consequences consistently, while preserving player agency and revision-fenced state.",
};

export function agentSystemPacksForStory(playMode: StoryPlayMode, settings: SystemPackSettings): AgentSystemPack[] {
  const modePack: AgentSystemPack = playMode === "freeform_novel"
    ? { id: "play_mode.freeform_novel", label: "Freeform Novel", enabled: true, instruction: "This is novel-first freeform play. ACDC and dice commands are unavailable. Resolve actions through fiction. Custom statuses may follow creator-defined rules; never impose combat turns, levels, HP, armor or initiative." }
    : { id: "play_mode.narrative_rpg", label: "Narrative RPG", enabled: true, instruction: "This is narrative RPG play using system-neutral mechanics declared in the project's File Types. Fiction and player agency lead; mechanics resolve uncertainty but never imply D&D rules by default." };
  return [modePack, ...systemPackIds.map((id) => ({ id, label: id.replaceAll("_", " "), enabled: settings[id] && !(playMode==='freeform_novel' && id==='dice'), instruction: optionalInstructions[id] }))];
}
import type { StoryPlayMode } from "./themes";
